import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatTabLink, MatTabNav, MatTabNavPanel } from '@angular/material/tabs';
import { ActivatedRoute, IsActiveMatchOptions, NavigationEnd, Router, RouterLink, RouterLinkActive, UrlTree } from '@angular/router';
import { filter } from 'rxjs';

export interface IZvNavigationTabItem {
  label: string;
  dataCy?: string;
  routerLink: (string | number)[];
  disabled?: boolean;
  children?: IZvNavigationTabItem[];
}
export interface IExtendedZvNavigationTabItem extends IZvNavigationTabItem {
  linkTree: UrlTree;
  children?: Omit<IExtendedZvNavigationTabItem, 'children'>[];
}

@Component({
  selector: 'zv-navigation-tabs',
  templateUrl: './navigation-tabs.component.html',
  styleUrls: ['./navigation-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTabNav, MatTabLink, RouterLink, MatButtonToggleGroup, MatButtonToggle, RouterLinkActive, MatTabNavPanel],
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class ZvNavigationTabs {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  readonly navLinks = input<IZvNavigationTabItem[]>([]);
  readonly navigationEndSignal = toSignal(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)), {
    initialValue: {} as NavigationEnd,
  });

  readonly navItems = computed(() =>
    this.navLinks()?.map((item) => ({
      ...item,
      linkTree: this.router.createUrlTree(item.routerLink, { relativeTo: this.route }),
      children: item.children?.map((child) => ({
        ...child,
        linkTree: this.router.createUrlTree(child.routerLink, { relativeTo: this.route }),
      })),
    }))
  );

  readonly active = computed(() => {
    // This is used as a reload trigger for this computed property
    this.navigationEndSignal();
    const active: { link: IZvNavigationTabItem | null; children: IZvNavigationTabItem[] } = { link: null, children: [] };
    for (const link of this.navItems()) {
      if (this.router.isActive(link.linkTree, this.routeMatchOptions)) {
        active.link = link;
        active.children = link.children || [];
        return active;
      }

      if (link.children) {
        for (const childLink of link.children) {
          if (this.router.isActive(childLink.linkTree, this.routeMatchOptions)) {
            active.link = link;
            active.children = link.children || [];
            return active;
          }
        }
      }
    }
    return active;
  });

  private routeMatchOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  public trackByLink(_index: number, item: IZvNavigationTabItem) {
    return item.routerLink.join('_');
  }
}
